import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { handleEnterNavKeyDown } from '../../lib/focusEnterNav';
import { XIcon } from './Icons';

interface TextInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix' | 'suffix' | 'onChange'> {
  value?: string;
  onChange?: (event: { target: { name?: string; value: string; type: string } }) => void;
  name?: string;
  placeholder?: string;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  type?: string;
  maxLength?: number;
  minLength?: number;
  showCharacterCount?: boolean;
  showClearButton?: boolean;
  autoComplete?: string;
  autoFocus?: boolean;
  pattern?: string;
  inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
  size?: 'sm' | 'default' | 'lg';
  variant?: 'default' | 'filled' | 'outlined' | 'underlined';
  error?: string;
  helperText?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  /** When true, Enter moves focus to the next field marked with data-enter-nav. */
  enterNav?: boolean;
}

const TextInput: React.FC<TextInputProps> = ({
  value,
  onChange,
  name,
  placeholder = 'Enter text',
  required = false,
  readOnly = false,
  disabled = false,
  className,
  type = 'text',
  maxLength,
  minLength,
  showCharacterCount = false,
  showClearButton = true,
  autoComplete,
  autoFocus = false,
  pattern,
  inputMode,
  size = 'default',
  variant = 'default',
  error,
  helperText,
  prefix,
  suffix,
  enterNav = false,
  onKeyDown,
  ...props
}) => {
  const { colors } = useTheme();
  const [inputValue, setInputValue] = useState(value || '');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);

    if (onChange) {
      onChange({
        target: {
          name,
          value: newValue,
          type: 'text',
        },
      });
    }
  };

  // Handle clear button
  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setInputValue('');

    if (onChange) {
      onChange({
        target: {
          name,
          value: '',
          type: 'text',
        },
      });
    }

    // Focus the input after clearing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle focus and blur
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (props.onFocus) {
      props.onFocus(event);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (props.onBlur) {
      props.onBlur(event);
    }
  };

  // Update input value when prop value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Size variants
  const sizeClasses = {
    sm: 'px-2 py-1 text-sm',
    default: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base',
  };

  // Variant styles
  const variantClasses = {
    default: `${colors.surface} ${colors.text} ${colors.border}`,
    filled: `${colors.surfaceHover} ${colors.text} border-transparent`,
    outlined: `bg-transparent ${colors.text} ${colors.border}`,
    underlined: `bg-transparent ${colors.text} border-0 border-b-2 ${colors.border} rounded-none`,
  };

  // Character count
  const characterCount = inputValue.length;
  const isOverLimit = maxLength && characterCount > maxLength;

  return (
    <div className="relative" data-enter-nav={enterNav ? '' : undefined}>
      {/* Main input container */}
      <div className="relative">
        {/* Prefix */}
        {prefix && (
          <div className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${colors.textMuted} pointer-events-none z-10`}>
            {prefix}
          </div>
        )}

        {/* Input field */}
        <input
          ref={inputRef}
          type={type}
          name={name}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            onKeyDown?.(e);
            if (!e.defaultPrevented && enterNav) {
              handleEnterNavKeyDown(e);
            }
          }}
          placeholder={placeholder}
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          maxLength={maxLength}
          minLength={minLength}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          pattern={pattern}
          inputMode={inputMode}
          className={cn(
            'w-full border rounded-lg transition-colors min-h-10',
            sizeClasses[size],
            variantClasses[variant],
            prefix ? 'pl-10' : '',
            suffix ? 'pr-10' : '',
            showClearButton && (value || inputValue) && !disabled && !readOnly ? 'pr-10' : '',
            isFocused ? cn('ring-2 ring-offset-0', colors.primaryBorderAll.replace('border-', 'ring-')) : '',
            error ? cn('border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20') : '',
            disabled ? 'opacity-50 cursor-not-allowed' : '',
            readOnly ? `cursor-default ${colors.surfaceHover}` : '',
            isOverLimit ? 'border-red-500' : '',
            className
          )}
          {...props}
        />

        {/* Suffix */}
        {suffix && (
          <div className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${colors.textMuted} pointer-events-none`}>
            {suffix}
          </div>
        )}

        {/* Clear button */}
        {showClearButton && (value || inputValue) && !disabled && !readOnly && (
          <button
            type="button"
            onClick={handleClear}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md transition-colors',
              colors.textMuted,
              colors.interactiveHover,
              'focus:outline-none focus:ring-2 focus:ring-offset-0',
              colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : ''
            )}
            title="Clear text"
          >
            <XIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Helper text and character count */}
      {(error || helperText || (showCharacterCount && maxLength)) && (
        <div className="mt-1 flex justify-between items-start">
          <div className="flex-1">
            {error && <div className={cn('text-sm', colors.error)}>{error}</div>}
            {!error && helperText && <div className={cn('text-sm', colors.textMuted)}>{helperText}</div>}
          </div>

          {showCharacterCount && maxLength && (
            <div
              className={cn('text-xs ml-2', isOverLimit ? colors.error : colors.textMuted)}
            >
              {characterCount}/{maxLength}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TextInput;

